// src/pages/company-head/DealerInterestLog.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../components/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  FaSearch, FaCalendar, FaUser, FaBox,
  FaClock, FaChartLine, FaEye, FaQrcode,
  FaMobileAlt, FaAngleLeft, FaAngleRight, FaTrophy,
  FaImage, FaWalking, FaCrosshairs, FaDesktop, FaLink,
  FaUsers, FaSortAmountDown, FaSortAmountUp, FaArrowRight,
  FaCubes
} from 'react-icons/fa';

const DealerInterestLog = () => {
  const { user } = useContext(AuthContext);
  const [reportMode, setReportMode] = useState('dealer'); // 'dealer' or 'product'
  const [logs, setLogs] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalLogs: 0, uniqueDealers: 0, views: 0, scans: 0,
    tileViews: 0, conceptProductViews: 0, sliderViews: 0, stockItemViews: 0, totalDuration: 0
  });
  const [topItems, setTopItems] = useState([]);

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Filters & pagination
  const today = new Date().toISOString().split('T')[0];
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [itemTypeFilter, setItemTypeFilter] = useState('');
  const [activityTypeFilter, setActivityTypeFilter] = useState('');
  const [pagination, setPagination] = useState({ totalPages: 1, total: 0 });
  const [sortBy, setSortBy] = useState('totalActivities');
  const [sortOrder, setSortOrder] = useState('desc');

  // Fetch product-wise logs
  const fetchProductLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);

      const params = { page, limit: 20 };
      if (searchTerm) params.search = searchTerm;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (itemTypeFilter) params.itemType = itemTypeFilter;
      if (activityTypeFilter) params.activityType = activityTypeFilter;

      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/company-heads/dealer-view-logs`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (data.success) {
        setLogs(data.data.logs);
        setStats(data.data.stats);
        setTopItems(data.data.topItems);
        setPagination(data.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching product logs:', error);
      Swal.fire('Error', 'Failed to fetch dealer activity logs', 'error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, startDate, endDate, itemTypeFilter, activityTypeFilter]);

  // Fetch dealer-wise report
  const fetchDealerReport = useCallback(async (page = 1) => {
    try {
      setLoading(true);

      const params = { page, limit: 20, sortBy, sortOrder };
      if (searchTerm) params.search = searchTerm;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (itemTypeFilter) params.itemType = itemTypeFilter;
      if (activityTypeFilter) params.activityType = activityTypeFilter;

      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/company-heads/dealer-view-logs/dealer-wise`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      if (data.success) {
        setDealers(data.data.dealers);
        setStats(data.data.stats);
        setPagination(data.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching dealer report:', error);
      Swal.fire('Error', 'Failed to fetch dealer-wise report', 'error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, startDate, endDate, itemTypeFilter, activityTypeFilter, sortBy, sortOrder]);

  // Fetch based on mode
  useEffect(() => {
    if (reportMode === 'dealer') {
      fetchDealerReport(currentPage);
    } else {
      fetchProductLogs(currentPage);
    }
  }, [currentPage, reportMode, fetchDealerReport, fetchProductLogs]);

  // Reset page on mode switch
  const switchMode = (mode) => {
    if (mode === reportMode) return;
    setReportMode(mode);
    setCurrentPage(1);
    setSearchTerm('');
    setStartDate(today);
    setEndDate(today);
    setItemTypeFilter('');
    setActivityTypeFilter('');
    setSortBy('totalActivities');
    setSortOrder('desc');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStartDate(today);
    setEndDate(today);
    setItemTypeFilter('');
    setActivityTypeFilter('');
    setCurrentPage(1);
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  // Format helpers
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatShortDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const timeAgo = (dateString) => {
    const diff = Date.now() - new Date(dateString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return formatShortDate(dateString);
  };

  // Badge components
  const ActivityBadge = ({ type }) => {
    const config = type === 'scan'
      ? { bg: darkMode ? 'bg-purple-900/40 text-purple-400 border border-purple-700' : 'bg-purple-100 text-purple-800', icon: <FaQrcode className="mr-1" />, label: 'QR Scan' }
      : { bg: darkMode ? 'bg-blue-900/40 text-blue-400 border border-blue-700' : 'bg-blue-100 text-blue-800', icon: <FaEye className="mr-1" />, label: 'View' };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg}`}>{config.icon}{config.label}</span>;
  };

  const ItemTypeBadge = ({ type }) => {
    const configs = {
      tile: { bg: darkMode ? 'bg-green-900/40 text-green-400 border border-green-700' : 'bg-green-100 text-green-800', icon: <FaBox className="mr-1" />, label: 'Tile' },
      conceptProduct: { bg: darkMode ? 'bg-orange-900/40 text-orange-400 border border-orange-700' : 'bg-orange-100 text-orange-800', icon: <FaImage className="mr-1" />, label: 'Concept' },
      slider: { bg: darkMode ? 'bg-cyan-900/40 text-cyan-400 border border-cyan-700' : 'bg-cyan-100 text-cyan-800', icon: <FaMobileAlt className="mr-1" />, label: 'Slider' },
      stockItem: { bg: darkMode ? 'bg-indigo-900/40 text-indigo-400 border border-indigo-700' : 'bg-indigo-100 text-indigo-800', icon: <FaCubes className="mr-1" />, label: 'Stock Item' }
    };
    const c = configs[type] || configs.tile;
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.bg}`}>{c.icon}{c.label}</span>;
  };

  const AccessMethodBadge = ({ method }) => {
    const configs = {
      browse: { icon: <FaWalking className="mr-1" />, label: 'Browse' },
      qr_scan: { icon: <FaQrcode className="mr-1" />, label: 'QR Scan' },
      search: { icon: <FaSearch className="mr-1" />, label: 'Search' },
      dashboard: { icon: <FaDesktop className="mr-1" />, label: 'Dashboard' },
      slider: { icon: <FaImage className="mr-1" />, label: 'Slider' },
      direct: { icon: <FaLink className="mr-1" />, label: 'Direct' }
    };
    const c = configs[method] || { icon: <FaCrosshairs className="mr-1" />, label: method || 'Unknown' };
    return (
      <span className={`inline-flex items-center text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        {c.icon}{c.label}
      </span>
    );
  };

  // Filter button helper
  const FilterBtn = ({ active, onClick, children }) => (
    <button onClick={onClick} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
      active ? 'bg-blue-500 text-white' : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
    }`}>{children}</button>
  );

  // Sortable header helper for dealer-wise table
  const SortHeader = ({ field, label }) => (
    <th
      onClick={() => toggleSort(field)}
      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none hover:text-blue-400 transition-colors ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortBy === field && (
          sortOrder === 'desc' ? <FaSortAmountDown className="text-blue-400 text-[10px]" /> : <FaSortAmountUp className="text-blue-400 text-[10px]" />
        )}
      </div>
    </th>
  );

  // Mini bar for dealer row breakdown
  const MiniBar = ({ views, scans, total }) => {
    if (!total) return null;
    const viewPct = (views / total * 100);
    const scanPct = (scans / total * 100);
    return (
      <div className={`flex h-1.5 rounded-full overflow-hidden w-20 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
        {viewPct > 0 && <div className="bg-blue-500 h-full" style={{ width: `${viewPct}%` }} />}
        {scanPct > 0 && <div className="bg-purple-500 h-full" style={{ width: `${scanPct}%` }} />}
      </div>
    );
  };

  if (loading && logs.length === 0 && dealers.length === 0) {
    return (
      <div className={`flex items-center justify-center h-64 ${darkMode ? 'bg-gray-900' : ''}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Loading report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 md:p-6 min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header + Mode Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <div>
          <h1 className={`text-2xl font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>App Dealer Interest Reports</h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Track dealer product views, QR scans, and engagement</p>
        </div>
        {/* Report Mode Toggle */}
        <div className={`inline-flex rounded-xl p-1 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-100'}`}>
          <button
            onClick={() => switchMode('dealer')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              reportMode === 'dealer'
                ? 'bg-blue-500 text-white shadow-sm'
                : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <FaUsers className="text-xs" /> Dealer Wise
          </button>
          <button
            onClick={() => switchMode('product')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              reportMode === 'product'
                ? 'bg-blue-500 text-white shadow-sm'
                : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <FaBox className="text-xs" /> Product Wise
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Activities', value: stats.totalLogs, icon: <FaChartLine />, color: 'red', border: 'border-red-500' },
          { label: 'Unique Dealers', value: stats.uniqueDealers, icon: <FaUser />, color: 'blue', border: 'border-blue-500' },
          { label: 'Views', value: stats.views, icon: <FaEye />, color: 'green', border: 'border-green-500' },
          { label: 'QR Scans', value: stats.scans, icon: <FaQrcode />, color: 'purple', border: 'border-purple-500' }
        ].map((card, i) => (
          <div key={i} className={`p-4 rounded-xl border-l-4 ${card.border} transition-colors ${
            darkMode ? 'bg-gray-800 border-t border-r border-b border-t-gray-700 border-r-gray-700 border-b-gray-700' : 'bg-white shadow-sm'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{card.label}</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{(card.value || 0).toLocaleString()}</p>
              </div>
              <div className={`text-${card.color}-500 text-xl`}>{card.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Item Type Breakdown + Top Items (Product mode only) */}
      {reportMode === 'product' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Item Type Breakdown */}
          <div className={`p-4 rounded-xl border transition-colors ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
            <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Activity by Item Type</h3>
            <div className="space-y-2">
              {[
                { label: 'Tiles', value: stats.tileViews, color: 'bg-green-500', total: stats.totalLogs },
                { label: 'Concept Products', value: stats.conceptProductViews, color: 'bg-orange-500', total: stats.totalLogs },
                { label: 'Stock Items', value: stats.stockItemViews, color: 'bg-indigo-500', total: stats.totalLogs },
                { label: 'Sliders', value: stats.sliderViews, color: 'bg-cyan-500', total: stats.totalLogs }
              ].map((item, i) => (
                <div key={i}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>{item.label}</span>
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{item.value} ({item.total > 0 ? Math.round(item.value / item.total * 100) : 0}%)</span>
                  </div>
                  <div className={`h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className={`h-2 rounded-full ${item.color} transition-all`} style={{ width: `${item.total > 0 ? (item.value / item.total * 100) : 0}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Items */}
          <div className={`p-4 rounded-xl border transition-colors ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
            <h3 className={`text-sm font-semibold mb-3 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <FaTrophy className="mr-2 text-yellow-500" /> Most Viewed Items
            </h3>
            {topItems.length > 0 ? (
              <div className="space-y-2">
                {topItems.map((item, i) => (
                  <div key={i} className={`flex items-center justify-between p-2 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center min-w-0 flex-1">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0 ${
                        i === 0 ? 'bg-yellow-500 text-white' : i === 1 ? 'bg-gray-400 text-white' : i === 2 ? 'bg-orange-600 text-white' : (darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-300 text-gray-600')
                      }`}>{i + 1}</span>
                      <div className="min-w-0">
                        <p className={`text-xs font-medium truncate ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{item.itemName || 'Unknown'}</p>
                        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{item.itemType === 'tile' ? 'Tile' : item.itemType === 'conceptProduct' ? 'Concept Product' : item.itemType === 'stockItem' ? 'Stock Item' : 'Slider'} · {item.uniqueDealers} dealers</p>
                      </div>
                    </div>
                    <span className={`text-sm font-bold ml-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{item.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className={`text-xs text-center py-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No data available</p>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={`p-4 rounded-xl border mb-6 transition-colors ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="flex flex-col gap-3">
          {/* Row 1: Search + Dates */}
          <div className="flex flex-col lg:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <FaSearch className={`absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  placeholder={reportMode === 'dealer' ? 'Search by dealer name, ID, or phone...' : 'Search by dealer name, item name, or ID...'}
                  value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </form>
            <div className="flex gap-2">
              <div className="relative">
                <FaCalendar className={`absolute left-3 top-1/2 -translate-y-1/2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                  className={`pl-9 pr-2 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 transition-colors ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white [color-scheme:dark]' : 'bg-white border-gray-300'
                  }`} />
              </div>
              <div className="relative">
                <FaCalendar className={`absolute left-3 top-1/2 -translate-y-1/2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                  className={`pl-9 pr-2 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 transition-colors ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white [color-scheme:dark]' : 'bg-white border-gray-300'
                  }`} />
              </div>
            </div>
          </div>

          {/* Row 2: Filter buttons */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Activity:</span>
            <FilterBtn active={!activityTypeFilter} onClick={() => { setActivityTypeFilter(''); setCurrentPage(1); }}>All</FilterBtn>
            <FilterBtn active={activityTypeFilter === 'view'} onClick={() => { setActivityTypeFilter('view'); setCurrentPage(1); }}>Views</FilterBtn>
            <FilterBtn active={activityTypeFilter === 'scan'} onClick={() => { setActivityTypeFilter('scan'); setCurrentPage(1); }}>Scans</FilterBtn>

            <span className={`text-xs font-medium ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Type:</span>
            <FilterBtn active={!itemTypeFilter} onClick={() => { setItemTypeFilter(''); setCurrentPage(1); }}>All</FilterBtn>
            <FilterBtn active={itemTypeFilter === 'tile'} onClick={() => { setItemTypeFilter('tile'); setCurrentPage(1); }}>Tiles</FilterBtn>
            <FilterBtn active={itemTypeFilter === 'conceptProduct'} onClick={() => { setItemTypeFilter('conceptProduct'); setCurrentPage(1); }}>Concepts</FilterBtn>
            <FilterBtn active={itemTypeFilter === 'slider'} onClick={() => { setItemTypeFilter('slider'); setCurrentPage(1); }}>Sliders</FilterBtn>
            <FilterBtn active={itemTypeFilter === 'stockItem'} onClick={() => { setItemTypeFilter('stockItem'); setCurrentPage(1); }}>Stock Items</FilterBtn>

            {(searchTerm || startDate || endDate || itemTypeFilter || activityTypeFilter) && (
              <button onClick={resetFilters} className="ml-auto px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 transition-colors">
                Clear All
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ==================== DEALER-WISE TABLE ==================== */}
      {reportMode === 'dealer' && (
        <div className={`rounded-xl border overflow-hidden transition-colors ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className={`px-4 py-2 border-b flex items-center justify-between ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-gray-50'}`}>
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {pagination.total} dealers {loading && '(loading...)'}
            </span>
            <div className="flex items-center gap-2">
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1"></span>Views
                <span className="inline-block w-2 h-2 rounded-full bg-purple-500 ml-2 mr-1"></span>Scans
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              <thead className={darkMode ? 'bg-gray-700/60' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Dealer</th>
                  <SortHeader field="totalActivities" label="Total" />
                  <SortHeader field="views" label="Views" />
                  <SortHeader field="scans" label="Scans" />
                  <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Breakdown</th>
                  <SortHeader field="uniqueProductCount" label="Products" />
                  <SortHeader field="lastActivity" label="Last Active" />
                  <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Access</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {dealers.map((d) => (
                  <tr key={d._id} className={`transition-colors ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}>
                    {/* Dealer */}
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm">
                          {(d.dealerName || 'U').charAt(0)}
                        </div>
                        <div className="ml-3 min-w-0">
                          <div className={`text-sm font-semibold truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{d.dealerName}</div>
                          <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {d.dealerId || ''}{d.dealerPhone ? ` · ${d.dealerPhone}` : ''}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Total */}
                    <td className="px-4 py-3">
                      <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{d.totalActivities}</span>
                    </td>

                    {/* Views */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <FaEye className="text-blue-500 text-xs" />
                        <span className={`text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{d.views}</span>
                      </div>
                    </td>

                    {/* Scans */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <FaQrcode className="text-purple-500 text-xs" />
                        <span className={`text-sm ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>{d.scans}</span>
                      </div>
                    </td>

                    {/* Breakdown bar */}
                    <td className="px-4 py-3">
                      <MiniBar views={d.views} scans={d.scans} total={d.totalActivities} />
                      <div className={`text-[10px] mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {d.tileViews > 0 && <span className="mr-1.5">T:{d.tileViews}</span>}
                        {d.conceptProductViews > 0 && <span className="mr-1.5">C:{d.conceptProductViews}</span>}
                        {d.stockItemViews > 0 && <span className="mr-1.5">SI:{d.stockItemViews}</span>}
                        {d.sliderViews > 0 && <span>S:{d.sliderViews}</span>}
                      </div>
                    </td>

                    {/* Products */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                      }`}>
                        <FaBox className="mr-1 text-[10px]" />{d.uniqueProductCount}
                      </span>
                    </td>

                    {/* Last Active */}
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <div className="flex items-center">
                        <FaClock className={`mr-1.5 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span title={formatDate(d.lastActivity)}>{timeAgo(d.lastActivity)}</span>
                      </div>
                      {d.firstActivity !== d.lastActivity && (
                        <div className={`text-[10px] flex items-center gap-1 mt-0.5 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                          {formatShortDate(d.firstActivity)} <FaArrowRight className="text-[8px]" /> {formatShortDate(d.lastActivity)}
                        </div>
                      )}
                    </td>

                    {/* Access Methods */}
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(d.accessMethods || []).map((m, i) => (
                          <AccessMethodBadge key={i} method={m} />
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty state */}
          {dealers.length === 0 && !loading && (
            <div className="text-center py-12">
              <FaUsers className={`mx-auto h-12 w-12 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
              <h3 className={`mt-2 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>No dealer activity found</h3>
              <p className={`mt-1 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Dealer activity will appear here as they interact with products in the mobile app.
              </p>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className={`px-4 py-3 border-t flex items-center justify-between ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Page {currentPage} of {pagination.totalPages}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 1 ? (darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                      : (darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                  }`}><FaAngleLeft className="inline mr-1" />Prev</button>
                <button onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))} disabled={currentPage === pagination.totalPages}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === pagination.totalPages ? (darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                      : (darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                  }`}>Next<FaAngleRight className="inline ml-1" /></button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== PRODUCT-WISE TABLE ==================== */}
      {reportMode === 'product' && (
        <div className={`rounded-xl border overflow-hidden transition-colors ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className={`px-4 py-2 border-b flex items-center justify-between ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-gray-50'}`}>
            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {pagination.total.toLocaleString()} results {loading && '(loading...)'}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              <thead className={darkMode ? 'bg-gray-700/60' : 'bg-gray-50'}>
                <tr>
                  {['Dealer', 'Item', 'Activity', 'Access Method', 'Time', 'Duration'].map(h => (
                    <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {logs.map((log) => (
                  <tr key={log._id} className={`transition-colors ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                          {(log.dealerName || 'U').charAt(0)}
                        </div>
                        <div className="ml-3 min-w-0">
                          <div className={`text-sm font-medium truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{log.dealerName}</div>
                          <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{log.dealerId || ''} {log.dealerPhone ? `· ${log.dealerPhone}` : ''}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="min-w-0">
                        <div className={`text-sm font-medium truncate max-w-[200px] ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{log.itemName || 'Unknown'}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <ItemTypeBadge type={log.itemType} />
                          {log.itemCode && <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{log.itemCode}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap"><ActivityBadge type={log.activityType} /></td>
                    <td className="px-4 py-3 whitespace-nowrap"><AccessMethodBadge method={log.accessMethod} /></td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <div className="flex items-center">
                        <FaClock className={`mr-1.5 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        {formatDate(log.activityAt)}
                      </div>
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {formatDuration(log.duration)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {logs.length === 0 && !loading && (
            <div className="text-center py-12">
              <FaEye className={`mx-auto h-12 w-12 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
              <h3 className={`mt-2 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>No activity logs found</h3>
              <p className={`mt-1 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Activity logs will appear here as dealers interact with products in the mobile app.
              </p>
            </div>
          )}

          {pagination.totalPages > 1 && (
            <div className={`px-4 py-3 border-t flex items-center justify-between ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Page {currentPage} of {pagination.totalPages}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === 1 ? (darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                      : (darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                  }`}><FaAngleLeft className="inline mr-1" />Prev</button>
                <button onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))} disabled={currentPage === pagination.totalPages}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    currentPage === pagination.totalPages ? (darkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                      : (darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                  }`}>Next<FaAngleRight className="inline ml-1" /></button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DealerInterestLog;
